import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import surToggleOverViewPdp from "@tCentro/Handlers/CENPDPOverViewBlockHandler";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OfferingModel {
    fields: {
        image: string;
        expertName: string;
        expertExperience: string;
        linkTitle: string;
        linkURL: LinkFieldValue;
        expertTime: string;
        topSectionRichContent: string;
        topSectionExpertImage: string;
        subContent: string;
    };
}

const enum fields {
    title = "title",
    description = "description",
    changeImage = "changeImage",
    startDate = "startDate",
    offerings = "offerings",
    contactNumber = "contactNumber",
    topSectionRichContent = "topSectionRichContent",
    topSectionExpertImage = "topSectionExpertImage",
    subContent = "subContent",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.changeImage]: string;
        [fields.startDate]: string;
        [fields.contactNumber]: number;
        [fields.offerings]: OfferingModel[];
        [fields.topSectionRichContent]: string;
        [fields.topSectionExpertImage]: string;
        [fields.subContent]: string;
    };
}

export interface NISLookingExpertPDPStyles {
    container?: InjectableCss;
    titleGridItem?: InjectableCss;
    imageGridItem?: InjectableCss;
    imageAndTitleSection?: InjectableCss;
    titileDescriptionGridItem?: InjectableCss;
    scheduleLinkStyle?: InjectableCss;
    expertTitle?: TypographyPresentationProps;
    expertExperience?: TypographyPresentationProps;
    expertTime?: TypographyPresentationProps;
    image?: LazyImageProps;
    chatButtonWrapper?: InjectableCss;
    numberStyles?: InjectableCss;
    topSectionContainer?: InjectableCss;
    topsectionTitle?: TypographyPresentationProps;
    subheadingTopsection?: TypographyPresentationProps;
    imgWrapper?: InjectableCss;
    subContentStyle?: TypographyPresentationProps;
    topSectionHeadingWrapper?: InjectableCss;
}

export const nisLookingExpertPDPStyles: NISLookingExpertPDPStyles = {
    titleGridItem: {
        css: css`
            display: flex;
            flex: 1;
            flex-direction: column;
            /* margin-left: 24px; */
            margin-bottom: 0px;
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 9px;
                    `,
                    css`
                        margin-bottom: 9px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    titileDescriptionGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    imageAndTitleSection: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            justify-content: start;
            gap: 20px;
        `,
    },
    subContentStyle: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 28px;
        `,
    },
    expertTitle: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
            font-size: ${TYPOGRAPHY.size.largest};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 32px;
            display: inline-flex;
            margin-bottom: 10px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.large};
                            line-height: 24px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )};
        `,
    },
    topsectionTitle: {
        css: css`
            font-weight: 600;
            font-size: 18px;
            color: ${getProp("theme.typography.p.color")};
            line-height: 24px;
            margin-bottom: 4px;
            font-family: Roboto Condensed;
            display: block;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [null, null, null, null], "max");
            }}
        `,
    },
    subheadingTopsection: {
        css: css`
            & p {
                font-size: ${TYPOGRAPHY.size.tiny};
                color: #444444;
                line-height: 16px;
                font-weight: 600;
                font-family: Roboto;
                ${({ theme }) => {
                return breakpointMediaQueries(theme, [null, null, null, null], "max");
            }}
            }
        `,
    },
    expertExperience: {
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 24px;
            margin-bottom: 12px;
        `,
    },
    expertTime: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 20px;
        `,
    },
    chatButtonWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-bottom: 8px;
        `,
    },
    scheduleLinkStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            border: 2px solid ${COLORS.secondary.base};
            border-radius: 24px;
            padding: 8px 12px;
            height: 32px;
            color: ${COLORS.secondary.base} !important;

            span {
                color: ${COLORS.secondary.base} !important;
            }
        `,
    },
    image: {
        css: css`
            aspect-ratio: 1/1;
            object-fit: contain;
            max-width: 104px;
            max-height: 104px;
            height: auto;

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-height: max-content;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    numberStyles: {
        css: css`
            color: ${getColor("text.link")};
            font-family: Poppins;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.base};
            line-height: 24px;
        `,
    },
    imgWrapper: {
        css: css`
            margin-right: 0px;
        `,
    },
    topSectionHeadingWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css``,
                        css`
                            flex-direction: column;
                            align-items: start !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

const mapStateToProps = (state: ApplicationState & CustomApplicationState, ownProps: OwnProps) => {
    return {
        isExpanded: state.t_components.overViewPdpBlock?.overViewPdpBlockisExpanded,
    };
};

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps>;

const styles = nisLookingExpertPDPStyles;
const NISPDPLookingForExpert: React.FC<Props> = ({ fields, isExpanded, product }) => {
    const container = {
        css: css`
            padding: 30px 30px 30px 30px;
            background: ${COLORS.background.grayShade};
            // height: ${isExpanded ? "max-content" : "100%"};
            display: flex;
            align-items: center;
            grid-template-columns: 70% 29%;
            justify-content: space-between;
            flex-direction: row;
            gap: 15px;
            @media (min-width: 992px) and (max-width: 1180px) {
                display: grid;
                grid-template-columns: 55% 43%;
            }

            @media (max-width: 991px) {
                flex-direction: column;
            }
        `,
    };

    const topSectionContainer = {
        css: css`
            background: #f0f0f0;
            height: 100%;
            padding: 8px 16px;
            display: flex;
            align-items: center;
            gap: 15px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-top: 4px;
                    `,
                    css`
                        margin-top: 4px;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    };
    const [chatLink, setChatLink] = useState<any>();
    const [offerringsUpdate, setOfferingData] = useState<any>();

    return (
        <StyledWrapper {...topSectionContainer}>
            <StyledWrapper {...styles.topSectionHeadingWrapper}>
                {fields.title && <Typography {...styles.topsectionTitle}>{fields.title}</Typography>}
                <Typography {...styles.subheadingTopsection}>
                    {parse(transformContentHtml(fields.topSectionRichContent), parserOptions)}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(NISPDPLookingForExpert)),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.topSectionRichContent,
                displayName: "Content",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
